import { useCallback, useMemo, useRef, useState } from 'react';
import mediaService from '../services/mediaService';

const normalizeQuery = (query) => (query || '').trim().toLowerCase();
const cacheKeyFor = (query, type, orientation) => `${normalizeQuery(query)}::${type || 'photo'}::${orientation || 'all'}`;

export default function useMedia({ initialQuery = 'marketing', initialType = 'photo' } = {}) {
  const cacheRef = useRef(new Map());

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState(initialQuery);
  const [type, setTypeState] = useState(initialType);
  const [orientation, setOrientationState] = useState('');

  const hydrateFromCache = useCallback((key) => {
    const cached = cacheRef.current.get(key);
    if (!cached) return false;

    setItems(cached.items || []);
    setPage(cached.page || 1);
    setHasMore(Boolean(cached.hasMore));
    return true;
  }, []);

  const writeCache = useCallback((key, nextState) => {
    cacheRef.current.set(key, {
      items: nextState.items,
      page: nextState.page,
      hasMore: nextState.hasMore,
    });
  }, []);

  const search = useCallback(async (nextQuery) => {
    const safeQuery = (nextQuery || '').trim();
    const key = cacheKeyFor(safeQuery, type, orientation);
    setQuery(safeQuery);
    setError('');

    if (hydrateFromCache(key)) return;

    setLoading(true);
    try {
      const data = await mediaService.searchMedia({ q: safeQuery || 'marketing', type, page: 1, orientation });
      if (data?.message) {
        setError(data.message);
      }
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      const nextPage = Number(data?.page || 1);
      const nextHasMore = Boolean(data?.has_more);

      setItems(nextItems);
      setPage(nextPage);
      setHasMore(nextHasMore);

      writeCache(key, {
        items: nextItems,
        page: nextPage,
        hasMore: nextHasMore,
      });
    } catch {
      setError(type === 'video' ? 'Não foi possível carregar vídeos' : 'Não foi possível carregar imagens');
      setItems([]);
      setPage(1);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [hydrateFromCache, orientation, type, writeCache]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    const nextPage = page + 1;
    const key = cacheKeyFor(query, type, orientation);

    setLoading(true);
    setError('');

    try {
      const data = await mediaService.searchMedia({ q: query || 'marketing', type, page: nextPage, orientation });
      if (data?.message) {
        setError(data.message);
      }
      const batch = Array.isArray(data?.items) ? data.items : [];
      const nextHasMore = Boolean(data?.has_more);

      setItems((previous) => {
        const merged = [...previous, ...batch];
        writeCache(key, {
          items: merged,
          page: nextPage,
          hasMore: nextHasMore,
        });
        return merged;
      });
      setPage(nextPage);
      setHasMore(nextHasMore);
    } catch {
      setError(type === 'video' ? 'Não foi possível carregar vídeos' : 'Não foi possível carregar imagens');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, orientation, page, query, type, writeCache]);

  const setType = useCallback((nextType) => {
    const normalized = nextType === 'video' ? 'video' : 'photo';
    setTypeState(normalized);

    const key = cacheKeyFor(query, normalized, orientation);
    if (!hydrateFromCache(key)) {
      setItems([]);
      setPage(1);
      setHasMore(true);
    }
  }, [hydrateFromCache, orientation, query]);

  const setOrientation = useCallback((nextOrientation) => {
    const normalized = nextOrientation || '';
    setOrientationState(normalized);

    const key = cacheKeyFor(query, type, normalized);
    if (!hydrateFromCache(key)) {
      setItems([]);
      setPage(1);
      setHasMore(true);
    }
  }, [hydrateFromCache, query, type]);

  return useMemo(() => ({
    items,
    loading,
    error,
    page,
    hasMore,
    query,
    type,
    orientation,
    search,
    loadMore,
    setType,
    setOrientation,
  }), [items, loading, error, page, hasMore, query, type, orientation, search, loadMore, setType, setOrientation]);
}
